<template>
  <v-tooltip bottom :open-delay="400" :disabled="!label">
    <template v-slot:activator="{ on, attrs }">
      <button
        class="qs-icon"
        :class="[stateClass, {
          'is-disabled': disabled,
          'has-slider': hasSlider,
          'has-action': hasAction,
          'has-children': hasChildren,
          [`qs-tag-${tagId}`]: !!tagId,
        }]"
        :style="colorStyle"
        v-bind="attrs"
        v-on="on"
        @click="handleClick"
        @contextmenu.prevent="handleContextMenu"
        :disabled="disabled"
      >
        <!-- Active glow ring -->
        <div v-if="showGlow" class="qs-icon-glow" />

        <!-- Icon container -->
        <div class="qs-icon-inner">
          <v-icon :size="iconSize">{{ icon }}</v-icon>
        </div>

        <!-- Slider indicator dot -->
        <div v-if="hasSlider" class="qs-slider-indicator" />

        <!-- Action indicator (cog rotation) -->
        <div v-if="hasAction" class="qs-action-indicator">
          <v-icon size="8">mdi-arrow-right</v-icon>
        </div>
      </button>
    </template>
    <span class="qs-tooltip">{{ label }}</span>
  </v-tooltip>
</template>

<script>
export default {
  name: 'QuickSettingsIcon',

  props: {
    icon: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    // Legacy boolean active state (for backward compatibility)
    active: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hasSlider: {
      type: Boolean,
      default: false,
    },
    hasAction: {
      type: Boolean,
      default: false,
    },
    // New props for color-coded tag buttons
    tagColor: {
      type: String,
      default: null, // If null, uses default primary color
    },
    state: {
      type: String,
      default: null, // 'off', 'partial', 'full' - if null, uses legacy active boolean
      validator: (v) => v === null || ['off', 'partial', 'full'].includes(v),
    },
    hasChildren: {
      type: Boolean,
      default: false,
    },
    tagId: {
      type: String,
      default: null,
    },
  },

  computed: {
    iconSize() {
      return this.hasAction ? 18 : 20;
    },

    // CSS custom properties for dynamic tag color
    colorStyle() {
      if (!this.tagColor) return {};
      return {
        '--tag-color': this.tagColor,
        '--tag-color-rgb': this.hexToRgb(this.tagColor),
      };
    },

    // Determine which CSS class to apply based on state or legacy active prop
    stateClass() {
      if (this.state) {
        return `state-${this.state}`;
      }
      return this.active ? 'is-active' : '';
    },

    // Show glow ring for active/full states (but not for action buttons)
    showGlow() {
      if (this.hasAction) return false;
      if (this.state) {
        return this.state === 'full' || this.state === 'partial';
      }
      return this.active;
    },
  },

  methods: {
    handleClick(event) {
      if (this.disabled) return;

      if (this.hasSlider) {
        this.$emit('slider-toggle', event);
      } else {
        this.$emit('click', event);
      }
    },

    handleContextMenu(event) {
      if (this.disabled) return;
      this.$emit('contextmenu', event);
    },

    // Convert hex color to RGB string for rgba() CSS functions
    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '120, 156, 74'; // Fallback to primary green
    },
  },
};
</script>

<style lang="scss" scoped>
// ============================================================================
// Quick Settings Icon - Precision Control Element
// With Light/Dark Theme Support
// ============================================================================

.qs-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--ds-radius-md);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  outline: none;

  // Remove default button styles
  -webkit-appearance: none;
  appearance: none;

  // Dark theme (default)
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);

  // Light theme
  .theme--light & {
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: rgba(0, 0, 0, 0.02);
  }

  &:hover:not(.is-disabled) {
    transform: translateY(-1px);

    // Dark theme
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.15);

    // Light theme
    .theme--light & {
      background: rgba(0, 0, 0, 0.06);
      border-color: rgba(0, 0, 0, 0.15);
    }

    .qs-icon-inner {
      .v-icon {
        // Dark theme
        color: rgba(255, 255, 255, 0.9);

        // Light theme
        .theme--light & {
          color: rgba(0, 0, 0, 0.8);
        }
      }
    }
  }

  &:active:not(.is-disabled) {
    transform: translateY(0);

    // Dark theme
    background: rgba(255, 255, 255, 0.05);

    // Light theme
    .theme--light & {
      background: rgba(0, 0, 0, 0.04);
    }
  }
}

// ============================================================================
// Icon Inner Container
// ============================================================================

.qs-icon-inner {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;

  .v-icon {
    transition: color 0.2s ease;

    // Dark theme
    color: rgba(255, 255, 255, 0.45);

    // Light theme
    .theme--light & {
      color: rgba(0, 0, 0, 0.45);
    }
  }
}

// ============================================================================
// Active State
// ============================================================================

.qs-icon.is-active {
  background: rgba(3, 98, 76, 0.12);
  border-color: rgba(3, 98, 76, 0.25);

  // Light theme - slightly stronger for visibility
  .theme--light & {
    background: rgba(3, 98, 76, 0.1);
    border-color: rgba(3, 98, 76, 0.3);
  }

  .qs-icon-inner .v-icon {
    color: var(--v-primary-base);
  }

  &:hover:not(.is-disabled) {
    background: rgba(3, 98, 76, 0.18);
    border-color: rgba(3, 98, 76, 0.35);

    .theme--light & {
      background: rgba(3, 98, 76, 0.15);
      border-color: rgba(3, 98, 76, 0.4);
    }
  }
}

// ============================================================================
// Active Glow Ring
// ============================================================================

.qs-icon-glow {
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: calc(var(--ds-radius-md) + 2px);
  background: transparent;
  border: 1px solid var(--v-primary-base);
  opacity: 0.3;
  animation: qsGlowPulse 2s ease-in-out infinite;
}

@keyframes qsGlowPulse {
  0%, 100% {
    opacity: 0.2;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(1.02);
  }
}

// ============================================================================
// Disabled State
// ============================================================================

.qs-icon.is-disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;

  .qs-icon-inner .v-icon {
    // Dark theme
    color: rgba(255, 255, 255, 0.25);

    // Light theme
    .theme--light & {
      color: rgba(0, 0, 0, 0.25);
    }
  }
}

// ============================================================================
// Slider Indicator
// ============================================================================

.qs-slider-indicator {
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 12px;
  height: 2px;
  border-radius: 1px;

  // Dark theme
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.3) 50%,
    transparent 100%
  );

  // Light theme
  .theme--light & {
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(0, 0, 0, 0.2) 50%,
      transparent 100%
    );
  }
}

.qs-icon.has-slider:hover .qs-slider-indicator {
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--v-primary-base) 50%,
    transparent 100%
  );
}

// ============================================================================
// Action Indicator (for PC Settings)
// ============================================================================

.qs-icon.has-action {
  border-style: dashed;

  // Dark theme
  background: rgba(255, 255, 255, 0.02);
  border-color: rgba(255, 255, 255, 0.1);

  // Light theme
  .theme--light & {
    background: rgba(0, 0, 0, 0.01);
    border-color: rgba(0, 0, 0, 0.1);
  }

  .qs-icon-inner .v-icon {
    // Dark theme
    color: rgba(255, 255, 255, 0.5);

    // Light theme
    .theme--light & {
      color: rgba(0, 0, 0, 0.5);
    }
  }

  &:hover:not(.is-disabled) {
    border-style: solid;

    // Dark theme
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.2);

    // Light theme
    .theme--light & {
      background: rgba(0, 0, 0, 0.05);
      border-color: rgba(0, 0, 0, 0.2);
    }

    .qs-action-indicator {
      opacity: 1;
      transform: translateX(0);
    }
  }
}

.qs-action-indicator {
  position: absolute;
  right: 3px;
  bottom: 3px;
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.2s ease;

  .v-icon {
    // Dark theme
    color: rgba(255, 255, 255, 0.5);

    // Light theme
    .theme--light & {
      color: rgba(0, 0, 0, 0.5);
    }
  }
}

// ============================================================================
// Tooltip
// ============================================================================

.qs-tooltip {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.3px;
}

// ============================================================================
// Color-Coded Tag Button System - Three Visual States
// ============================================================================

.qs-icon {
  // CSS custom property defaults for dynamic tag color
  --tag-color: #03624c; // Default fallback to primary green
  --tag-color-rgb: 120, 156, 74;

  // Dropdown indicator for parent buttons with children
  &.has-children {
    &::after {
      content: '';
      position: absolute;
      bottom: 4px;
      right: 4px;
      width: 0;
      height: 0;
      border-left: 3px solid transparent;
      border-right: 3px solid transparent;
      border-top: 3px solid currentColor;
      opacity: 0.35;
      transition: opacity 0.2s ease;
    }

    &:hover::after {
      opacity: 0.6;
    }
  }
}

// ============================================================================
// OFF State - Border only, dimmed
// ============================================================================
.qs-icon.state-off {
  background: transparent;
  border: 1.5px solid var(--tag-color);
  border-style: dashed; // Dashed border indicates "disabled/off" state
  opacity: 0.5;

  .qs-icon-inner .v-icon {
    color: var(--tag-color) !important;
  }

  &:hover:not(.is-disabled) {
    opacity: 0.85;
    background: rgba(var(--tag-color-rgb), 0.12); // Increased from 0.08
    border-style: solid; // Solid border on hover shows "ready to activate"
    border-color: var(--tag-color);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(var(--tag-color-rgb), 0.15);
  }

  // Light theme
  .theme--light & {
    opacity: 0.6;

    &:hover:not(.is-disabled) {
      opacity: 0.9;
      background: rgba(var(--tag-color-rgb), 0.15); // Increased from 0.1
      box-shadow: 0 2px 8px rgba(var(--tag-color-rgb), 0.2);
    }
  }
}

// ============================================================================
// PARTIAL State - Translucent fill (some children active)
// ============================================================================
.qs-icon.state-partial {
  background: rgba(var(--tag-color-rgb), 0.2);
  border: 1.5px solid var(--tag-color);

  .qs-icon-inner .v-icon {
    color: var(--tag-color) !important;
  }

  // Pulsing glow ring for partial state
  .qs-icon-glow {
    border-color: var(--tag-color);
    opacity: 0.25;
    animation: partialPulse 2.5s ease-in-out infinite;
  }

  &:hover:not(.is-disabled) {
    background: rgba(var(--tag-color-rgb), 0.32); // Increased from 0.28
    border-width: 2px; // Slightly thicker border on hover
    transform: translateY(-1px);
    box-shadow: 0 2px 10px rgba(var(--tag-color-rgb), 0.25);

    .qs-icon-glow {
      opacity: 0.5;
    }

    .qs-icon-inner .v-icon {
      filter: brightness(1.1); // Slightly brighter icon on hover
    }
  }

  // Light theme
  .theme--light & {
    background: rgba(var(--tag-color-rgb), 0.15);

    &:hover:not(.is-disabled) {
      background: rgba(var(--tag-color-rgb), 0.28); // Increased from 0.22
      box-shadow: 0 2px 10px rgba(var(--tag-color-rgb), 0.3);
    }
  }
}

@keyframes partialPulse {
  0%, 100% { opacity: 0.2; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(1.015); }
}

// ============================================================================
// FULL State - Solid color fill (all children active)
// ============================================================================
.qs-icon.state-full {
  background: var(--tag-color);
  border: 1.5px solid var(--tag-color);
  box-shadow:
    0 0 12px rgba(var(--tag-color-rgb), 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);

  .qs-icon-inner .v-icon {
    color: #ffffff !important;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  }

  // Stronger glow ring for full state
  .qs-icon-glow {
    border-color: var(--tag-color);
    opacity: 0.5;
    box-shadow: 0 0 8px var(--tag-color);
  }

  &:hover:not(.is-disabled) {
    transform: translateY(-1px);
    filter: brightness(1.15); // Brighten solid color for clear hover feedback
    box-shadow:
      0 0 20px rgba(var(--tag-color-rgb), 0.6),
      0 4px 16px rgba(0, 0, 0, 0.25),
      inset 0 1px 0 rgba(255, 255, 255, 0.25);

    .qs-icon-inner .v-icon {
      color: #5f5f5f !important;
      filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.3));
    }

    .qs-icon-glow {
      opacity: 0.7;
    }
  }

  // Light theme - slightly less intense for readability
  .theme--light & {
    box-shadow:
      0 0 10px rgba(var(--tag-color-rgb), 0.35),
      0 2px 8px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);

    &:hover:not(.is-disabled) {
      filter: brightness(1.1); // Slightly less brightness in light theme
      box-shadow:
        0 0 16px rgba(var(--tag-color-rgb), 0.45),
        0 4px 12px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.25);
    }
  }
}
</style>
